/**
 * MODEL PRECISION round (Matt, 2026-10-08): "Any dimension tick that is set to a model
 * element, including gridlines, should be removed when that element is deleted."
 *
 * Before this, a witness whose element/grid/drafting record was deleted degraded to its
 * stored point and stayed in the string — Santiago's grid strings each still carried a
 * tick on a grid deleted long ago, measuring to a line that no longer exists.
 *
 * Now: the moment a transaction deletes a record any dimension witness points at, that
 * witness leaves its string (a post-transact pass, so every delete path is covered —
 * element delete, grid delete, cut, undo of a create, a peer-free bulk delete). A string
 * left with fewer than two witnesses is deleted. A radius/arc dimension whose element is
 * gone is deleted. Per-segment text placements/overrides are re-keyed to the surviving
 * witness indices so they stay on the segments they were set on. `pruneOrphanWitnesses`
 * is the one-time sweep for models that already carry dead ticks (run on open).
 */
import * as Y from 'yjs'
import { maps, registerPostTransactHook, type ModelDoc } from './doc'
import type { DraftingWitness } from './types'

/** the record a witness is anchored to, or null for a free (static) witness */
function witnessTarget(w: DraftingWitness | null | undefined): { map: 'elements' | 'grids' | 'drafting'; id: string } | null {
  if (!w) return null
  const elId = w.ref?.elementId ?? w.lineRef?.elementId ?? w.footRef?.elementId ?? w.openingRef?.elementId
  if (elId) return { map: 'elements', id: elId }
  if (w.gridRef?.gridId) return { map: 'grids', id: w.gridRef.gridId }
  if (w.draftRef?.recId) return { map: 'drafting', id: w.draftRef.recId }
  return null
}

/** re-key "i:j" per-segment entries onto the surviving witness indices; entries that
 *  touch a removed witness are dropped. Non-segment keys ("r", "arc") pass through. */
function rekey<T>(rec: Record<string, T> | null | undefined, newIndex: Map<number, number>): Record<string, T> | null {
  if (!rec) return rec ?? null
  const out: Record<string, T> = {}
  for (const [k, v] of Object.entries(rec)) {
    const m = /^(\d+):(\d+)$/.exec(k)
    if (!m) { out[k] = v; continue }
    const a = newIndex.get(Number(m[1])), b = newIndex.get(Number(m[2]))
    if (a == null || b == null) continue
    out[`${Math.min(a, b)}:${Math.max(a, b)}`] = v
  }
  return Object.keys(out).length ? out : null
}

/** Remove every witness anchored to a record that no longer exists. Returns the number
 *  of dimension records changed or deleted. Call inside a kernel transact. */
export function pruneOrphanWitnesses(model: ModelDoc): number {
  const m = maps(model)
  const alive = (t: { map: 'elements' | 'grids' | 'drafting'; id: string }) => m[t.map].has(t.id)
  const deletes: string[] = []
  const edits: [Y.Map<unknown>, string, unknown][] = []
  m.drafting.forEach((rec, id) => {
    if (!(rec instanceof Y.Map) || rec.get('kind') !== 'dimension') return
    const edgeRef = rec.get('edgeRef') as { elementId?: string } | null | undefined
    // (a `draft:<id>` edge is a drafted arc — DRAFTING PARITY 2026-10-12)
    if (edgeRef?.elementId && (edgeRef.elementId.startsWith('draft:')
      ? !m.drafting.has(edgeRef.elementId.slice(6))
      : !m.elements.has(edgeRef.elementId))) { deletes.push(id); return }
    const list = rec.get('witnesses') as DraftingWitness[] | undefined
    if (Array.isArray(list)) {
      const newIndex = new Map<number, number>()
      const kept: DraftingWitness[] = []
      list.forEach((w, i) => {
        const t = witnessTarget(w)
        if (t && !alive(t)) return
        newIndex.set(i, kept.length)
        kept.push(w)
      })
      if (kept.length === list.length) return
      if (kept.length < 2) { deletes.push(id); return }
      edits.push([rec, 'witnesses', kept])
      if (rec.get('textOffsets')) edits.push([rec, 'textOffsets', rekey(rec.get('textOffsets') as Record<string, unknown>, newIndex)])
      if (rec.get('textOverrides')) edits.push([rec, 'textOverrides', rekey(rec.get('textOverrides') as Record<string, unknown>, newIndex)])
      if (rec.get('eq') && kept.length < 3) edits.push([rec, 'eq', false])
      return
    }
    // pre-r22 two-point form
    for (const k of ['a', 'b'] as const) {
      const t = witnessTarget(rec.get(k) as DraftingWitness | undefined)
      if (t && !alive(t)) { deletes.push(id); return }
    }
  })
  for (const [rec, k, v] of edits) rec.set(k, v)
  for (const id of deletes) m.drafting.delete(id)
  return new Set([...deletes, ...edits.map(([rec]) => rec)]).size
}

/** post-transact: only when this transaction removed an element, grid or drafting record */
function pruneOnDelete(model: ModelDoc, tr?: Y.Transaction): void {
  if (!tr) return
  const m = maps(model)
  for (const name of ['elements', 'grids', 'drafting'] as const) {
    const keys = tr.changed.get(m[name] as unknown as Y.AbstractType<Y.YEvent<any>>)
    if (!keys) continue
    for (const k of keys) {
      if (k && !m[name].has(k)) { pruneOrphanWitnesses(model); return }
    }
  }
}

registerPostTransactHook(pruneOnDelete)
